import React, { useState, useEffect, useMemo } from 'react';
import { apiClient, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  Plus,
  Gavel,
  Euro,
  Wrench,
  Clock,
  MoreHorizontal,
  X,
  Trash2,
  Edit2
} from 'lucide-react';

const EVENT_TYPES = {
  ag: { label: 'Assemblée Générale', color: 'bg-purple-500', icon: Gavel },
  appel_fonds: { label: 'Échéance Appel', color: 'bg-blue-500', icon: Euro },
  travaux: { label: 'Travaux', color: 'bg-orange-500', icon: Wrench },
  echeance: { label: 'Échéance', color: 'bg-red-500', icon: Clock },
  autre: { label: 'Autre', color: 'bg-slate-500', icon: MoreHorizontal }
};

const Calendrier = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    titre: '',
    description: '',
    date_debut: '',
    type_event: 'autre'
  });

  const canManage = user?.role && ['président', 'secrétaire'].includes(user.role);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await apiClient.get('/calendar/events');
      setEvents(response.data || []);
    } catch (error) {
      console.error('Erreur chargement événements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.titre || !newEvent.date_debut) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await apiClient.post('/calendar/events', {
        titre: newEvent.titre,
        description: newEvent.description,
        date_debut: new Date(newEvent.date_debut).toISOString(),
        type_event: newEvent.type_event
      });
      toast.success('Événement créé');
      setCreateModal(false);
      setNewEvent({ titre: '', description: '', date_debut: '', type_event: 'autre' });
      fetchEvents();
    } catch (error) {
      const message = getErrorMessage(error, "Erreur lors de la création");
      if (message) toast.error(message);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Supprimer cet événement ?')) return;

    try {
      await apiClient.delete(`/calendar/events/${eventId}`);
      toast.success('Événement supprimé');
      setDetailModal(false);
      fetchEvents();
    } catch (error) {
      const message = getErrorMessage(error, "Erreur lors de la suppression");
      if (message) toast.error(message);
    }
  };

  const openCreateForDate = (date) => {
    setNewEvent({
      titre: '',
      description: '',
      date_debut: format(date, 'yyyy-MM-dd'),
      type_event: 'autre'
    });
    setCreateModal(true);
  };

  const openEventDetail = (event) => {
    setSelectedEvent(event);
    setDetailModal(true);
  };

  // Générer les jours du mois
  const monthDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Ajouter les jours du mois précédent pour remplir la première semaine
    const startDay = start.getDay();
    const daysToAdd = startDay === 0 ? 6 : startDay - 1; // Lundi = premier jour
    
    const prevMonthDays = [];
    for (let i = daysToAdd; i > 0; i--) {
      const date = new Date(start);
      date.setDate(date.getDate() - i);
      prevMonthDays.push(date);
    }
    
    // Ajouter les jours du mois suivant pour compléter la grille
    const totalDays = prevMonthDays.length + days.length;
    const nextMonthDays = [];
    const remaining = totalDays % 7 === 0 ? 0 : 7 - (totalDays % 7);
    
    for (let i = 1; i <= remaining; i++) {
      const date = new Date(end);
      date.setDate(date.getDate() + i);
      nextMonthDays.push(date);
    }
    
    return [...prevMonthDays, ...days, ...nextMonthDays];
  }, [currentMonth]);

  // Grouper les événements par date
  const eventsByDate = useMemo(() => {
    const grouped = {};
    events.forEach(event => {
      const dateStr = event.date_debut?.split('T')[0];
      if (dateStr) {
        if (!grouped[dateStr]) grouped[dateStr] = [];
        grouped[dateStr].push(event);
      }
    });
    return grouped;
  }, [events]);

  const getEventsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return eventsByDate[dateStr] || [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="calendrier-page">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarIcon className="w-7 h-7 text-amber-500" />
            Calendrier
          </h1>
          <p className="text-slate-500 mt-1">
            Événements de la copropriété
          </p>
        </div>
        {canManage && (
          <Button
            onClick={() => setCreateModal(true)}
            data-testid="create-event-button"
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un événement
          </Button>
        )}
      </div>

      {/* Légende des couleurs */}
      <div className="flex flex-wrap gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
        {Object.entries(EVENT_TYPES).map(([key, val]) => (
          <div key={key} className="flex items-center gap-2 text-sm">
            <div className={`w-3 h-3 rounded-full ${val.color}`}></div>
            <span className="text-slate-600">{val.label}</span>
          </div>
        ))}
      </div>

      {/* Navigation du mois */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h2 className="text-lg font-semibold text-slate-900">
            {format(currentMonth, 'MMMM yyyy', { locale: fr })}
          </h2>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Jours de la semaine */}
        <div className="grid grid-cols-7 border-b border-slate-200">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-slate-500 bg-slate-50">
              {day}
            </div>
          ))}
        </div>

        {/* Grille des jours */}
        <div className="grid grid-cols-7">
          {monthDays.map((date, index) => {
            const isCurrentMonth = isSameMonth(date, currentMonth);
            const isToday = isSameDay(date, new Date());
            const dayEvents = getEventsForDate(date);

            return (
              <div
                key={index}
                className={`min-h-24 p-1 border-b border-r border-slate-100 ${
                  isCurrentMonth ? 'bg-white' : 'bg-slate-50'
                } ${canManage ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                onClick={() => canManage && openCreateForDate(date)}
              >
                <div className={`text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                  isToday ? 'bg-amber-500 text-white' : isCurrentMonth ? 'text-slate-900' : 'text-slate-400'
                }`}>
                  {format(date, 'd')}
                </div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map(event => {
                    const eventType = EVENT_TYPES[event.type_event] || EVENT_TYPES.autre;
                    return (
                      <div
                        key={event.id}
                        onClick={(e) => { e.stopPropagation(); openEventDetail(event); }}
                        className={`${eventType.color} text-white text-xs px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80`}
                        title={event.titre}
                      >
                        {event.titre}
                      </div>
                    );
                  })}
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-slate-500 pl-1">
                      +{dayEvents.length - 3} autre(s)
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Liste des événements à venir */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-3">Prochains événements</h3>
        <div className="space-y-2">
          {events
            .filter(e => new Date(e.date_debut) >= new Date())
            .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut))
            .slice(0, 5)
            .map(event => {
              const eventType = EVENT_TYPES[event.type_event] || EVENT_TYPES.autre;
              const EventIcon = eventType.icon;
              return (
                <div
                  key={event.id}
                  onClick={() => openEventDetail(event)}
                  className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow"
                >
                  <div className={`w-10 h-10 ${eventType.color} rounded-lg flex items-center justify-center`}>
                    <EventIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{event.titre}</p>
                    <p className="text-sm text-slate-500">
                      {format(parseISO(event.date_debut), 'EEEE d MMMM yyyy', { locale: fr })}
                    </p>
                  </div>
                  {event.auto_generated && (
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded">Auto</span>
                  )}
                </div>
              );
            })}
          {events.filter(e => new Date(e.date_debut) >= new Date()).length === 0 && (
            <p className="text-slate-500 text-center py-8">Aucun événement à venir</p>
          )}
        </div>
      </div>

      {/* Modal de création */}
      <Dialog open={createModal} onOpenChange={setCreateModal}>
        <DialogContent className="max-w-md" data-testid="create-event-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-500" />
              Nouvel événement
            </DialogTitle>
            <DialogDescription>
              Ajoutez un événement au calendrier de la copropriété
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={newEvent.titre}
                onChange={(e) => setNewEvent({ ...newEvent, titre: e.target.value })}
                placeholder="Ex: Réunion syndic, Travaux façade..."
                data-testid="event-titre-input"
              />
            </div>

            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={newEvent.date_debut}
                onChange={(e) => setNewEvent({ ...newEvent, date_debut: e.target.value })}
                data-testid="event-date-input"
              />
            </div>

            <div>
              <Label>Type d'événement</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Object.entries(EVENT_TYPES).filter(([k]) => k !== 'ag' && k !== 'appel_fonds').map(([key, val]) => {
                  const isSelected = newEvent.type_event === key;
                  const Icon = val.icon;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setNewEvent({ ...newEvent, type_event: key })}
                      className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                        isSelected ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className={`w-8 h-8 ${val.color} rounded flex items-center justify-center`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm font-medium">{val.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Description (optionnel)</Label>
              <Textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                placeholder="Détails supplémentaires..."
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={() => setCreateModal(false)} variant="outline" className="flex-1">
                Annuler
              </Button>
              <Button
                onClick={handleCreateEvent}
                disabled={!newEvent.titre || !newEvent.date_debut}
                className="flex-1 bg-amber-500 hover:bg-amber-600"
                data-testid="save-event-button"
              >
                Créer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de détail */}
      <Dialog open={detailModal} onOpenChange={setDetailModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Détail de l'événement</DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4 mt-4">
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 ${EVENT_TYPES[selectedEvent.type_event]?.color || 'bg-slate-500'} rounded-lg flex items-center justify-center`}>
                  {React.createElement(EVENT_TYPES[selectedEvent.type_event]?.icon || MoreHorizontal, { className: "w-6 h-6 text-white" })}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg">{selectedEvent.titre}</h3>
                  <p className="text-slate-500">
                    {format(parseISO(selectedEvent.date_debut), 'EEEE d MMMM yyyy', { locale: fr })}
                  </p>
                </div>
              </div>

              {selectedEvent.description && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-sm text-slate-700">{selectedEvent.description}</p>
                </div>
              )}

              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span className={`px-2 py-1 rounded ${
                  selectedEvent.auto_generated ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
                }`}>
                  {selectedEvent.auto_generated ? 'Automatique' : 'Manuel'}
                </span>
                <span className="px-2 py-1 bg-slate-100 rounded">
                  {EVENT_TYPES[selectedEvent.type_event]?.label || 'Autre'}
                </span>
              </div>

              {canManage && !selectedEvent.auto_generated && (
                <div className="pt-2 border-t">
                  <Button
                    onClick={() => handleDeleteEvent(selectedEvent.id)}
                    variant="outline"
                    className="w-full text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer l'événement
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Calendrier;
